import { QueryFactory, useEnabledDataBlockTypes } from '@lmt/shared-types';
import { DataBlockVariant } from '@lmt/shared-types';
import { UIEditorLazy } from '@lmt/unicorn-shared/app/components/editor/UIEditorLazy';
import { patientInformationDataBlockTypeName } from '@lmt/unicorn-shared/app/components/measurements/common';
import { PatientData } from '@lmt/unicorn-shared/app/components/patients/patient-form/common/types';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UseFormReturn } from 'react-hook-form';
import { titleKey } from '../common/common';
import { UIEditorDataObjectValue } from '../../../editor/UIEditorTypes';

type Props = {
  patientForm: UseFormReturn<PatientData>;
  wrapper?: React.FC<React.PropsWithChildren>;
  onIsValidChange?: (isValid: boolean) => void;
  showCustomFieldsErrors: boolean;

  // this method shall be called before saving inputValues/sharedValues
  // it saves the ongoing edits into inputValues/sharedValues (especially needed for Tables)
  getCustomFieldsBeforeSave: React.Ref<() => Promise<any>>;
};
export const CustomPatientFields: React.FC<Props> = React.memo(
  function CustomPatientFields(props) {
    const i18next = useScopedTranslation('patients.form');
    const dataBlockTypes = useEnabledDataBlockTypes();
    const patientInformationDataBlockType = dataBlockTypes.find(
      (x) =>
        x.variant === DataBlockVariant.Patient &&
        x.value === patientInformationDataBlockTypeName,
    );
    if (!patientInformationDataBlockType) {
      throw new Error(i18next.t('errors.data_block_type_not_found'));
    }
    const configQuery = QueryFactory.DataBlockTypeQuery.useGetUIConfigsQuery(
      patientInformationDataBlockType.id,
    );

    const patientCustomFieldsFromProps = props.patientForm.getValues('values');
    const [patientCustomFields, setPatientCustomFields] = useState(
      patientCustomFieldsFromProps,
    );
    const dataBlockValuesUnchanged = useRef('');
    useEffect(() => {
      if (
        patientCustomFieldsFromProps &&
        JSON.stringify(patientCustomFieldsFromProps) !==
          dataBlockValuesUnchanged.current
        // we compare received JSON with initial JSON values,
        // and if they are the same, we will not update (since it might be modified by user already)
      ) {
        dataBlockValuesUnchanged.current = JSON.stringify(
          patientCustomFieldsFromProps,
        );
        setPatientCustomFields(JSON.parse(dataBlockValuesUnchanged.current));
      }
    }, [patientCustomFieldsFromProps]);

    useEffect(() => {
      props.patientForm.register('values');
    }, [props.patientForm]);

    const [errors, setErrors] = useState<{
      [key: string]: string;
    }>({});
    const changeErrors = useCallback(
      (key: string, error: string) => {
        // calling props.form.setError/clearError didn't work out (since form uses yup)

        if (error) {
          setErrors((prevErrors) => {
            props.onIsValidChange?.(false);
            return { ...prevErrors, [key]: error };
          });
        } else {
          setErrors((prevErrors) => {
            delete prevErrors[key];
            if (Object.keys(prevErrors).length === 0) {
              props.onIsValidChange?.(true);
            }
            return { ...prevErrors };
          });
        }
      },
      [props.patientForm],
    );

    // we need this because otherwise setErrors are not called (they check if InputValues are loaded or not)
    const inputValuesStub = useMemo(() => ({}), []);

    const beforeUiEditorSaveRef = useRef<() => Promise<void>>(null);
    useImperativeHandle(
      props.getCustomFieldsBeforeSave,
      () => async () => {
        if (beforeUiEditorSaveRef.current) {
          await beforeUiEditorSaveRef.current();
        }
        setPatientCustomFields((old: any) => {
          const currentCustomFields = props.patientForm.getValues('values');
          if (currentCustomFields[titleKey]) {
            old[titleKey] = currentCustomFields[titleKey];
          } else if (old[titleKey]) {
            old[titleKey] = undefined;
          }
          props.patientForm.setValue('values', old);
          return old;
        });
        return props.patientForm.getValues('values');
      },
      [],
    );

    if (!configQuery.data?.view) {
      // Custom UI wasn't configured
      return null;
    }

    const content = (
      <UIEditorLazy
        isInDesignerMode={false}
        isOnlyView={false}
        dataBlockTypeNameForLocalization={'patient.information'}
        dataBlockType={patientInformationDataBlockType}
        config={configQuery.data.view}
        sharedValues={patientCustomFields}
        changeSharedValues={(key: string, value: UIEditorDataObjectValue) => {
          // we need to update state & trigger rerender to support a scenario when there are 2 Fields bound to the same value (they should update synchronously)
          setPatientCustomFields((customFields: any) => ({
            ...customFields,
            [key]: value,
          }));
          props.patientForm.setValue(`values.${key}`, value);
        }}
        inputValues={inputValuesStub}
        changeErrors={changeErrors}
        inputErrors={errors}
        beforeSaveRef={beforeUiEditorSaveRef}
        showErrors={props.showCustomFieldsErrors}
      />
    );
    const Wrapper = props.wrapper;
    return Wrapper ? <Wrapper>{content}</Wrapper> : content;
  },
);
